import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { CartProvider } from "@/context/CartContext";
import { WhatsAppButton, ChatbotWidget } from "@/components/widgets/FloatingWidgets";
import { CookieConsent } from "@/components/widgets/CookieConsent";
import CookiePolicy from "@/pages/CookiePolicy";
import ServiceLanding from "@/pages/ServiceLanding";
import { PhoneGate } from "@/components/PhoneGate";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import CampaignView from "@/pages/CampaignView";
import MyCampaigns from "@/pages/MyCampaigns";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Security from "@/pages/Security";
import HubPage from "@/pages/HubPage";
import GuidePage from "@/pages/GuidePage";
import { HUBS } from "@/lib/seoHubs";
import { markNoIndex } from "@/lib/seo";
import { useEffect } from "react";

const PRIVATE = ["/login", "/register", "/forgot-password", "/reset-password", "/admin", "/account", "/payment", "/campaign", "/auth"];

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    if (PRIVATE.some((p) => location.pathname.startsWith(p))) markNoIndex("Area riservata | AURORA-ADV, marketing Swiss Made");
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/termini-e-condizioni" element={<Terms />} />
      <Route path="/privacy-policy" element={<Privacy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/sicurezza-e-privacy" element={<Security />} />
      {HUBS.map((h) => <Route key={h.slug} path={`/${h.slug}`} element={<HubPage slug={h.slug} />} />)}
      {["de", "fr"].flatMap((l) => HUBS.map((h) => <Route key={`${l}-${h.slug}`} path={`/${l}/${h.slug}`} element={<HubPage key={`${l}-${h.slug}`} slug={h.slug} lang={l} />} />))}
      <Route path="/guide" element={<GuidePage />} />
      <Route path="/guide/:slug" element={<GuidePage />} />
      <Route path="/servizi" element={<ServiceLanding />} />
      <Route path="/servizi/:slug" element={<ServiceLanding />} />
      <Route path="/campaign/:id" element={
        <ProtectedRoute><CampaignView /></ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute><MyCampaigns /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requireRole="super_admin"><Admin /></ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <LangProvider>
            <CartProvider>
              <AppRouter />
              <PhoneGate />
              <ChatbotWidget />
              <WhatsAppButton />
              <CookieConsent />
            </CartProvider>
          </LangProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
